import type { FC } from 'react';
import { useContext, useCallback, memo, useRef } from 'react';
import { y2r } from '@gridsheet/web';
import { getLabel } from '@gridsheet/web';
import { between, zoneToArea } from '@gridsheet/web';
import { Context } from '../store';
import {
  choose,
  drag,
  select,
  selectRows,
  setAutofillDraggingTo,
  setContextMenuPosition,
  setDragging,
  setEditingAddress,
  setResizingPositionY,
  setRowMenu,
  submitAutofill,
  write,
} from '../store/actions';
import { DEFAULT_HEIGHT } from '@gridsheet/web';
import { operations as prevention } from '@gridsheet/web';
import { insertRef } from '@gridsheet/web';
import { focus } from '@gridsheet/web';
import { isXSheetFocused } from '../store/helpers';
import { ScrollHandle } from './ScrollHandle';
import { isTouching, safePreventDefault } from '../lib/events';
import { useDebounceCallback } from '../lib/hooks';

type Props = {
  y: number;
};

export const HeaderCellLeft: FC<Props> = memo(({ y }) => {
  const rowId = `${y2r(y)}`;
  const { store, dispatch } = useContext(Context);

  const {
    choosing,
    editingAddress,
    selectingZone,
    leftHeaderSelecting,
    editorRef,
    sheetReactive: sheetRef,
    autofillDraggingTo,
    dragging,
    contextMenu,
    rowMenuState,
  } = store;
  const sheet = sheetRef.current;

  const row = sheet?.getCell({ y, x: 0 }, { resolution: 'SYSTEM' });
  const height = row?.height || DEFAULT_HEIGHT;

  const xSheetFocused = isXSheetFocused(store);
  const lastFocused = sheet?.registry.lastFocused;

  const editingAnywhere = !!(sheet?.registry.editingAddress || editingAddress);

  const writeCell = useCallback(
    (value: string) => {
      dispatch(write({ value, point: choosing }));
    },
    [choosing],
  );

  const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
    dispatch(setResizingPositionY([y, e.clientY, e.clientY]));
    e.stopPropagation();
    safePreventDefault(e);
  }, []);

  const handleDragStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.stopPropagation();
      safePreventDefault(e);

      if (!isTouching(e) || !sheet) {
        return false;
      }
      if (dragging) {
        return false;
      }

      // Single row selection only for touch events
      if (e.type.startsWith('touch')) {
        // Blur the input field to commit current value when selecting via touch
        if (editingAnywhere && editorRef.current) {
          editorRef.current.blur();
        }
        dispatch(choose({ y, x: 1 }));
        dispatch(select({ startY: y, startX: 1, endY: y, endX: sheet.numCols }));
        return true;
      }

      // Normal drag operation for mouse events
      dispatch(select({ startY: y, startX: 1, endY: y, endX: -1 }));
      const fullAddress = `${sheet.sheetPrefix(!xSheetFocused)}${rowId}:${rowId}`;
      if (editingAnywhere) {
        const inserted = insertRef({ input: lastFocused || null, ref: fullAddress });
        if (inserted) {
          dispatch(select({ startY: y, startX: sheet.numCols, endY: y, endX: 0 }));
          return false;
        }
      }

      let startY = e.shiftKey ? selectingZone.startY : y;
      if (startY === -1) {
        startY = choosing.y;
      }

      dispatch(
        selectRows({
          range: { start: startY, end: y },
          numCols: sheet.numCols,
        }),
      );

      if (editingAnywhere) {
        writeCell(lastFocused?.value ?? '');
      }
      dispatch(setEditingAddress(''));
      dispatch(setDragging(true));
      focus(editorRef.current);

      if (autofillDraggingTo) {
        return false;
      }
      return true;
    },
    [
      dragging,
      editingAnywhere,
      xSheetFocused,
      rowId,
      lastFocused,
      selectingZone,
      choosing,
      autofillDraggingTo,
      editorRef,
    ],
  );

  const handleDragEnd = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.stopPropagation();
      if (e.type.startsWith('touch')) {
        return;
      }

      safePreventDefault(e);
      dispatch(setDragging(false));
      if (autofillDraggingTo) {
        focus(editorRef.current);
        return false;
      }
    },
    [autofillDraggingTo],
  );

  const handleDragging = useDebounceCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!isTouching(e) || !sheet) {
      return false;
    }

    // Do nothing for touch events
    if (e.type.startsWith('touch')) {
      return false;
    }

    safePreventDefault(e);
    e.stopPropagation();

    if (autofillDraggingTo) {
      dispatch(setAutofillDraggingTo({ y, x: 1 }));
      return false;
    }

    if (editingAnywhere) {
      const newArea = zoneToArea({ ...selectingZone, endY: y, endX: 1 });
      const [top, bottom] = [y2r(newArea.top), y2r(newArea.bottom)];
      const fullRange = `${sheet.sheetPrefix(!xSheetFocused)}${top}:${bottom}`;
      insertRef({ input: lastFocused || null, ref: fullRange });
    }

    if (autofillDraggingTo == null) {
      const { startX } = selectingZone;
      if (startX === 1) {
        dispatch(drag({ y, x: sheet.numCols }));
      } else {
        dispatch(drag({ y, x: 1 }));
      }
    }
    return false;
  }, 100);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement>) => {
      if (contextMenu.length > 0) {
        e.stopPropagation();
        safePreventDefault(e);
        dispatch(setContextMenuPosition({ y: e.clientY, x: e.clientX }));
        return false;
      }
      return true;
    },
    [contextMenu.length],
  );

  if (!sheet) {
    return null;
  }

  return (
    <th
      data-y={y}
      className={`gs-th gs-th-left ${choosing.y === y ? 'gs-choosing' : ''} ${
        between({ start: selectingZone.startY, end: selectingZone.endY }, y)
          ? leftHeaderSelecting
            ? 'gs-th-selecting'
            : 'gs-selecting'
          : ''
      } ${row?.filterFixed ? 'gs-filter-fixed' : ''} ${row?.sortFixed ? 'gs-sort-fixed' : ''}`}
      style={{ ...row?.style, height }}
      onContextMenu={handleContextMenu}
    >
      <div
        className="gs-th-inner-wrap"
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        onMouseEnter={handleDragging}
        onMouseUp={handleDragEnd}
      >
        <div className="gs-th-inner" style={{ width: sheet.headerWidth, position: 'relative' }}>
          <ScrollHandle
            style={{
              position: 'absolute',
              zIndex: leftHeaderSelecting ? -1 : 1,
            }}
            horizontal={-1}
          />
          {getLabel(sheet, row?.label, { y, x: 0 }, y) ?? rowId}
          {!prevention.hasOperation(row?.prevention, prevention.RowMenu) && (
            <button
              className={`gs-menu-btn gs-row-menu-btn ${rowMenuState?.y === y ? 'gs-active' : ''}`}
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
                (e.currentTarget as HTMLElement).dataset.pressX = String(e.clientX);
                (e.currentTarget as HTMLElement).dataset.pressY = String(e.clientY);
              }}
              onMouseUp={(e) => {
                e.stopPropagation();
                const btn = e.currentTarget as HTMLElement;
                const pressX = Number(btn.dataset.pressX ?? e.clientX);
                const pressY = Number(btn.dataset.pressY ?? e.clientY);
                const moved = Math.abs(e.clientX - pressX) > 4 || Math.abs(e.clientY - pressY) > 4;
                if (moved) {
                  return; // was a drag, ignore
                }
                const rect = btn.getBoundingClientRect();
                if (rowMenuState?.y === y) {
                  dispatch(setRowMenu(null));
                } else {
                  const alreadySelected =
                    between({ start: selectingZone.startY, end: selectingZone.endY }, y) &&
                    selectingZone.startX === 1 &&
                    selectingZone.endX === sheet.numCols;
                  if (!alreadySelected) {
                    dispatch(selectRows({ range: { start: y, end: y }, numCols: sheet.numCols }));
                  }
                  dispatch(setRowMenu({ y, position: { y: rect.bottom, x: rect.right } }));
                }
              }}
            >
              ⋮
            </button>
          )}
          <div
            className={`
              gs-resizer
              ${prevention.hasOperation(row?.prevention, prevention.Resize) ? 'gs-protected' : ''}
              ${dragging ? 'gs-hidden' : ''}`}
            style={{ width: sheet.headerWidth }}
            onMouseDown={handleResizeMouseDown}
          ></div>
        </div>
      </div>
    </th>
  );
});
